import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { BaseComponent } from '@portals/core';
import { FilterDataType, Filter, NestedKey, FilterColumn } from '@portals/api';
import { Title } from '@angular/platform-browser';
import { TableComponent } from '@portals/ui';
import { Router } from '@angular/router';
import { ENTER } from '@angular/cdk/keycodes';
import { environment } from '@portals/environments';

@Component({
	selector: 'portals-advanced-filter',
	templateUrl: './advanced-filter.component.html',
	styleUrls: ['./advanced-filter.component.scss'],
})
export class AdvancedFilterComponent extends BaseComponent {
	filterDataTypes: FilterDataType[] = [];
	filters: Filter[] = [];
	title: string;
	app = environment.app;
	filterColumns: FilterColumn[] = [];
	fieldNameCheck: {
		[key: string]: {
			fieldName: string;
			entityName: string;
		};
	} = {};
	defaultFilter = {
		parameter: { text: '', key: '', value: '', nestedKey: '' },
		condition: '',
		value: '',
		rangeValue: [],
		field: '',
		collectionType: '',
	};
	booleanConditions = ['Is', 'Is not'];
	stringConditions = ['Is', 'Is not', 'Contains', `Does not contain`];
	numberConditions = ['Equals', 'Less than', 'Greater than', 'Does not equal', 'Less than or equal to', 'Greater than or equal to'];
	durationConditions = ['Is', 'Greater than', 'Less than', 'Between'];
	dateConditions = ['Is before', 'Is after', 'Is between'];
	locationConditions = ['Is within', 'Is not within'];
	containsOnlyConditions = ['Contains'];

	@Output() clearAllFilters = new EventEmitter<any>();

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public dialogData: {
			filterDataTypes: Array<FilterDataType>;
			filters: Array<Filter>;
		},
		private titleService: Title,
		private router: Router,
		public dialogRef: MatDialogRef<AdvancedFilterComponent>
	) {
		super();
		this.title = titleService.getTitle();
		this.filterDataTypes = [...this.dialogData.filterDataTypes];
		this.filters = this.dialogData.filters.length ? [...this.dialogData.filters] : [this.defaultFilter];
		this.filterColumns = this.getColumnOptions();
		this.filterDataTypes.forEach((data: FilterDataType) => {
			this.fieldNameCheck[data.key] = {
				fieldName: data.fieldName || '',
				entityName: data.entityName || '',
			};
		});
	}

	getColumnOptions(): FilterColumn[] {
		const selectedFilterKeys = [...this.filters.map(({ parameter: { key } }: Filter) => key)];
		const unselectedFilters = this.filterDataTypes.reduce((filtered: FilterColumn[], option) => {
			let unselectedFilter: FilterColumn = {
				key: option.key,
				text: option.text,
				disabled: false,
			};
			if (selectedFilterKeys.includes(option.key)) {
				unselectedFilter = { ...unselectedFilter, disabled: true };
			}
			filtered.push(unselectedFilter);
			return filtered;
		}, []);
		return unselectedFilters;
	}

	onColumnChange(value: string, i: number) {
		const selectedFilterData = this.filterDataTypes.find(({ key }) => key === value);

		if (selectedFilterData) {
			const { condition, key, text, field, collectionType } = selectedFilterData;
			let filters = [...this.filters];
			filters[i] = {
				...this.defaultFilter,
				field,
				parameter: { key, text, value: condition },
				collectionType,
			};

			this.filters = filters;
		}

		this.filterColumns = this.getColumnOptions();
	}

	isColumnEmpty(i: number) {
		return !Boolean(this.filters[i].parameter.key);
	}

	isNestedColumnEmpty(filterKey: string): boolean {
		const selectedFilterData = this.filterDataTypes.find(({ key }) => key === filterKey);
		if (selectedFilterData) {
			const { nestedKeys } = selectedFilterData || {};
			return !(nestedKeys || []).length;
		}
		return true;
	}

	getNestedColumnOptions(filterKey: string): NestedKey[] {
		const selectedFilterData = this.filterDataTypes.find(({ key }) => key === filterKey);
		if (selectedFilterData) {
			const { nestedKeys } = selectedFilterData || {};
			return nestedKeys || [];
		}
		return [];
	}

	onNestedColumnChange(value: string, i: number) {
		let filters = [...this.filters];
		filters[i] = {
			...filters[i],
			parameter: { ...filters[i].parameter, nestedKey: value },
			condition: '',
			value: '',
			rangeValue: [],
		};
		this.filters = filters;
	}

	getConditions(i: number) {
		const value = this.filters[i].parameter.value;
		let conditions = [];

		switch (value) {
			case 'text':
				conditions = this.stringConditions;
				break;
			case 'number':
				conditions = this.numberConditions;
				break;
			case 'duration':
				conditions = this.durationConditions;
				break;
			case 'date':
				conditions = this.dateConditions;
				break;
			case 'boolean':
				conditions = this.booleanConditions;
				break;
			case 'contains':
				conditions = this.containsOnlyConditions;
				break;
			case 'location':
				conditions = this.locationConditions;
				break;
			default:
				conditions = this.stringConditions;
		}
		return conditions;
	}

	onConditionChange(value: string, i: number) {
		let filters = [...this.filters];
		filters[i] = {
			...filters[i],
			condition: value,
			value: '',
			rangeValue: [],
		};
		this.filters = filters;
	}

	isConditionEmpty(i: number) {
		return !Boolean(this.filters[i].condition);
	}

	isCustomField(i: number) {
		const { field } = this.filters[i];
		return ['select', 'multiselect', 'duration', 'toggle', 'location', 'moment', 'async-dropdown'].includes(field || '');
	}

	getValueType(i: number) {
		return this.filters[i].parameter.value;
	}

	getValuePlaceholder(i: number) {
		return this.filters[i].parameter.text;
	}

	isConditionIsBetween(i: number) {
		return Boolean(['Is between', 'Between'].includes(this.filters[i].condition));
	}

	onChangeValue(event: any, i: number, isEnd: boolean = false) {
		const { target } = event || {};
		let filters = [...this.filters];

		if (Boolean(target)) {
			if (!this.isConditionIsBetween(i)) {
				filters[i] = { ...filters[i], value: target.value };
			} else {
				if (!isEnd) {
					filters[i] = {
						...filters[i],
						rangeValue: [target.value, filters[i].rangeValue[1]],
					};
				} else {
					filters[i] = {
						...filters[i],
						rangeValue: [filters[i].rangeValue[0], target.value],
					};
				}
			}
		} else {
			filters[i] = { ...filters[i], value: event };
		}

		this.filters = filters;
	}

	onChangeCustomFieldValue(event: any, i: number) {
		const { value, subValue = [], isMultiSelect = false, isRange = false } = event || {};
		let filters = [...this.filters];

		if (!isMultiSelect && !isRange) {
			filters[i] = { ...filters[i], value };
		} else {
			filters[i] = { ...filters[i], rangeValue: value, subValue };
		}

		this.filters = filters;
	}

	addFilter() {
		this.filters = [...this.filters, this.defaultFilter];
	}

	removeFilter(i: number) {
		const filters = [...this.filters.filter((_, index) => index !== i)];
		this.filters = !filters.length ? [this.defaultFilter] : filters;
		this.filterColumns = this.getColumnOptions();
	}

	isSaveDisabled() {
		let disabled = true;
		for (let index = 0; index < this.filters.length; index++) {
			const { value, rangeValue } = this.filters[index];
			if (!(value === undefined || value === null || value === '') || Boolean(rangeValue.length)) {
				disabled = false;
				break;
			}
		}
		return disabled;
	}

	getFilterOptions(parameterKey: string): any[] {
		const selectedFilterData = this.filterDataTypes.find(({ key }) => key === parameterKey);
		if (selectedFilterData) {
			const { options = [] } = selectedFilterData || {};
			return options;
		}
		return [];
	}

	getMultiselectEntityName(parameterKey: string): string {
		const selectedFilterData = this.filterDataTypes.find(({ key }) => key === parameterKey);
		if (selectedFilterData) {
			const { entityName = '' } = selectedFilterData || {};
			return entityName;
		}
		return '';
	}

	getMultiselectFieldName(parameterKey: string): string {
		const selectedFilterData = this.filterDataTypes.find(({ key }) => key === parameterKey);
		if (selectedFilterData) {
			const { fieldName = '' } = selectedFilterData || {};
			return fieldName;
		}
		return '';
	}

	isOptionString(option: string | object) {
		return typeof option === 'string';
	}

	applyFilters() {
		const validatedFilters = [...this.filters.filter(({ value, rangeValue }) => !(value === undefined || value === null || value === '') || Boolean(rangeValue.length))];
		this.dialogRef.close(validatedFilters);
	}

	trackByFn(index: any, item: any) {
		return index;
	}

	clearFilters() {
		this.filters = [this.defaultFilter];
	}

	onValueFieldEnter(event: any, i: number) {
		const { target } = event || {};
		if (target.value) {
			this.onChangeValue(event, i);
			this.applyFilters();
		}
	}
}
